import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Settings, PencilLine } from 'lucide-react';
import type { Chart } from '@shared/schemas/chart';
import {
  getChartIconComponent,
} from '../../lib/chartUtils';
import UniversalChartRenderer from '../charts/UniversalChartRenderer';
import { normalizeChartData } from '../../lib/chartDataNormalizer';
import { convertDatabaseChartToUniversal } from '@shared/schemas/chart';
import ChartDataEditorDialog from '../charts/ChartDataEditorDialog';
import EditableChartTitle from '../charts/EditableChartTitle';
import { ChartService } from '../../services/chartService';
import { useAuth } from '../../contexts/AuthContext';
import { currentOrganizationAtom } from '../../store/organizationAtoms';
import { useAtom } from 'jotai';
import { useCustomPalettes } from '../../contexts/ChartPreferencesContext';
// import { useActiveDefaultPaletteId } from '../../contexts/ChartPreferencesContext';  // Temporarily commented out
import { useTheme } from '../../lib/themeUtils';

// Original simpler props before chart favoriting
interface DisplayChartCardProps {
  chart: Chart; // EnrichedChartForDisplay can be simplified to Chart if order/isFavorite are handled by parent for this specific card
  onRemoveChart: (chartId: string) => void;
  onOpenStudies: (chart: Chart) => void;
  onChartDataUpdate: () => void;
  hideTitle?: boolean; // Keep this if Home page might sometimes want NO header at all
  hideEditButton?: boolean; // Option to hide the edit data button (for home screen)
}

const DisplayChartCard: React.FC<DisplayChartCardProps> = ({
  chart,
  onRemoveChart,
  onOpenStudies,
  onChartDataUpdate,
  hideTitle = false,
  hideEditButton = false,
}) => {
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [isDataEditorOpen, setIsDataEditorOpen] = useState(false);
  const HeaderIcon = getChartIconComponent(chart.type);
  const { currentUser } = useAuth();
  const [currentOrganization] = useAtom(currentOrganizationAtom);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const customPalettes = useCustomPalettes();
  // const activeDefaultPaletteId = useActiveDefaultPaletteId();
  const isDarkMode = useTheme();

  const handleSaveTitle = async (newTitle: string) => {
    if (!currentUser || !currentOrganization || !chart || !chart.id) {
      console.error('Cannot save title: missing auth context, org, or chart details.');
      return;
    }
    if (newTitle === chart.title) return;

    setIsSavingTitle(true);
    try {
      await ChartService.update(currentOrganization.id, currentUser.uid, chart.id, { title: newTitle });
      if (onChartDataUpdate) {
        onChartDataUpdate();
      }
    } catch (error) {
      console.error('Error saving chart title:', error);
    } finally {
      setIsSavingTitle(false);
    }
  };

  const renderActualChart = () => {
    // Convert database chart to universal format first
    const universalChart = convertDatabaseChartToUniversal(chart);
    
    // Then normalize for rendering
    const config = normalizeChartData(universalChart, { 
      height: 350,
      customPalettes,
      isDarkMode,
      enableAdvancedFeatures: true,
    });
    
    return <UniversalChartRenderer config={config} />;
  };

  return (
    <Card className="flex flex-col h-full hover:shadow-md transition-shadow">
      {!hideTitle && (
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3 px-4 pt-3"> {/* Main flex row */}
          {/* Left: Icon & Title */}
          <div className="flex items-center gap-2 flex-1 min-w-0" onPointerDown={(e) => e.stopPropagation()}> {/* Takes available space, allows title to truncate */}
            {HeaderIcon && <HeaderIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
            <EditableChartTitle
              title={chart.title || 'Untitled Chart'}
              onSave={handleSaveTitle}
              textClassName="text-lg font-semibold text-foreground"
              inputClassName="text-lg font-semibold"
              disabled={isSavingTitle}
              isLoading={isSavingTitle}
            />
          </div>

          {/* Right: Actions - Pushed to the right by justify-between on CardHeader */}
          <div className="flex-shrink-0 flex items-center gap-0.5">
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onPointerDown={(e) => e.stopPropagation()} onClick={() => onOpenStudies(chart)} title="Chart Studies">
              <Settings className="h-4 w-4" />
            </Button>
            {!hideEditButton && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onPointerDown={(e) => e.stopPropagation()} onClick={() => setIsDataEditorOpen(true)} title="Edit Chart Data">
                <PencilLine className="h-4 w-4" />
              </Button>
            )}
            <AlertDialog open={isRemoveConfirmOpen} onOpenChange={setIsRemoveConfirmOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/20" onPointerDown={(e) => e.stopPropagation()} title="Remove from Dashboard">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent onPointerDown={(e) => e.stopPropagation()}>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action will remove the chart "{chart.title || 'Untitled Chart'}" from this dashboard. It will not delete the original chart.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => { onRemoveChart(chart.id); }}>Remove</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardHeader>
      )}
      {hideTitle && ( // This minimal header is for when NO title/header elements are wanted, just actions if any.
        <CardHeader className="flex-row items-center justify-end gap-0.5 pb-2 pt-2 pr-2">
           {/* Minimal actions if needed when title is hidden - currently Settings & Trash */}
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onPointerDown={(e) => e.stopPropagation()} onClick={() => onOpenStudies(chart)} title="Chart Studies"><Settings className="h-4 w-4" /></Button>
            {!hideEditButton && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onPointerDown={(e) => e.stopPropagation()} onClick={() => setIsDataEditorOpen(true)} title="Edit Chart Data">
                <PencilLine className="h-4 w-4" />
              </Button>
            )}
            <AlertDialog open={isRemoveConfirmOpen} onOpenChange={setIsRemoveConfirmOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/20" onPointerDown={(e) => e.stopPropagation()} title="Remove from Dashboard"><Trash2 className="h-4 w-4" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent onPointerDown={(e) => e.stopPropagation()}>
                <AlertDialogHeader><AlertDialogTitle>Are you sure?</AlertDialogTitle><AlertDialogDescription>This action will remove the chart "{chart.title || 'Untitled Chart'}" from this dashboard. It will not delete the original chart.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { onRemoveChart(chart.id); }}>Remove</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
        </CardHeader>
      )}
      <CardContent className={`flex-grow flex items-center justify-center p-2 ${hideTitle ? 'pt-1' : ''}`}> 
        {renderActualChart()}
      </CardContent>

      {/* Chart Data Editor Dialog */}
      {chart && (
        <ChartDataEditorDialog
          isOpen={isDataEditorOpen}
          onClose={() => setIsDataEditorOpen(false)}
          chart={chart}
          onChartUpdate={onChartDataUpdate} 
        />
      )}
    </Card>
  );
};

export default DisplayChartCard; 